import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Mail,
  Phone,
  Building,
  MapPin,
  Calendar,
  Edit,
  Save,
  X,
  Star,
  Settings,
  Shield,
  Check,
  Loader,
  AlertTriangle,
  Camera,
  Briefcase,
  GraduationCap,
  Award,
  FileText,
  Trophy,
  Zap,
  ArrowLeft
} from 'lucide-react';
import React from 'react';
import CandidateProfileTabs from '@/components/candidate/CandidateProfileTabs';
import CandidateHeader from '@/components/common/CandidateHeader';
import ResumeUploadWidget from '@/components/candidates/ResumeUploadWidget';
import { apiClient, handleApiError } from '@/lib/api';

interface CandidateProfile {
  candidate_uuid: string;
  current_title?: string;
  current_company?: string;
  current_location?: string;
  summary?: string;
  linkedin_url?: string;
  github_url?: string;
  portfolio_url?: string;
  avatar_url?: string;
  user_uuid?: string;
  created_at?: string;
  updated_at?: string;
}

interface ProfileData {
  basic?: CandidateProfile;
  education?: {
    education: any[];
    total_count: number;
  };
  work_experience?: {
    work_experience: any[];
    total_count: number;
    total_years?: number;
  };
  skills?: {
    skills: any[];
    total_count: number;
    primary_skills?: any[];
  };
  awards_achievements?: {
    awards_achievements: any[];
    total_count: number;
  };
  certifications?: {
    certifications: any[];
    total_count: number;
  };
}

export default function CandidateProfilePage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [candidateUuid, setCandidateUuid] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showTabs, setShowTabs] = useState(false);
  const [extractedResumeData, setExtractedResumeData] = useState<any | null>(null);
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    setIsLoggedIn(!!token);
    
    if (userData) {
      const user = JSON.parse(userData);
      setCurrentUser({ ...user, token });
    }
    
    const uuid = searchParams.get('uuid');
    if (uuid) {
      setCandidateUuid(uuid);
    } else if (token) {
      setCandidateUuid('me');
    }
  }, [searchParams]);

  useEffect(() => {
    if (candidateUuid) {
      loadProfileData();
    }
  }, [candidateUuid]);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use apiClient for proper authentication and error handling
      const [basicRes, educationRes, workExpRes, skillsRes, awardsRes, certsRes] = await Promise.all([
        apiClient.request('/candidates/me/profile/basic'),
        apiClient.request('/education/me/profile'),
        apiClient.request('/work-exp/me/profile'),
        apiClient.request('/skills/me/profile'),
        apiClient.request('/awards/me/profile'),
        apiClient.request('/certs/me/profile')
      ]);

      // Cast responses to any to avoid strict typing issues from apiClient
      const b: any = basicRes;
      const e: any = educationRes;
      const w: any = workExpRes;
      const s: any = skillsRes;
      const a: any = awardsRes;
      const c: any = certsRes;

      setProfile({
        basic: b.success ? b.data : undefined,
        education: e.success ? e.data : { education: [], total_count: 0 },
        work_experience: w.success ? w.data : { work_experience: [], total_count: 0 },
        skills: s.success ? s.data : { skills: [], total_count: 0 },
        awards_achievements: a.success ? a.data : { awards_achievements: [], total_count: 0 },
        certifications: c.success ? c.data : { certifications: [], total_count: 0 }
      });

    } catch (err) {
      console.error('Failed to load profile data:', err);
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile?.basic) return;

    setSaving(true);
    try {
      const endpoint = candidateUuid === 'me' ? 
        '/candidates/me/profile/basic' : 
        `/candidates/${candidateUuid}/profile/basic`;
      
      const response = await apiClient.request(endpoint, {
        method: 'PUT',
        body: JSON.stringify(profile.basic)
      });
      
      if (response.success) {
        setIsEditing(false);
        // Show success message
        const successMsg = document.createElement('div');
        successMsg.className = 'fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50';
        successMsg.innerHTML = '<strong>Success!</strong> Profile updated successfully.';
        document.body.appendChild(successMsg);
        setTimeout(() => document.body.removeChild(successMsg), 3000);
      } else {
        throw new Error(response.error || 'Failed to update profile');
      }
    } catch (err) {
      const errorMsg = document.createElement('div');
      errorMsg.className = 'fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50';
      errorMsg.innerHTML = `<strong>Error!</strong> ${handleApiError(err)}`;
      document.body.appendChild(errorMsg);
      setTimeout(() => document.body.removeChild(errorMsg), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof CandidateProfile, value: any) => {
    setProfile(prev => ({
      ...prev,
      basic: prev?.basic ? { ...prev.basic, [field]: value } : undefined
    } as ProfileData | null));
  };

  const handleQuickAction = (action: string) => {
    // For now, we'll navigate to the detailed tabs view
    // In the future, this could open modals or in-place editors
    setShowTabs(true);
  };

  const handleBackToOverview = () => {
    setShowTabs(false);
  };

  if (!candidateUuid) {
    return (
      <div className="min-h-screen bg-gray-50">
        {isLoggedIn && <CandidateHeader />}
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {isLoggedIn && <CandidateHeader />}
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        {isLoggedIn && <CandidateHeader />}
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Profile</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button onClick={loadProfileData} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If showTabs is true, render the detailed tabs view
  if (showTabs) {
    return (
      <div className="min-h-screen bg-gray-50">
        {isLoggedIn && <CandidateHeader />}
        <div className={`${isLoggedIn ? 'pt-4' : 'py-8'}`}>
          <div className="max-w-4xl mx-auto px-4 mb-4">
            <button 
              onClick={handleBackToOverview}
              className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Profile Overview
            </button>
          </div>
          <div className="max-w-4xl mx-auto px-4 mb-6">
            {/* Resume upload to populate profile tabs */}
            <ResumeUploadWidget
              mode="full-parse"
              onDataParsed={(data: any) => {
                console.log('Parsed resume data received on profile page', data);
                // The resume parser API returns fields slightly different; normalize if needed
                setExtractedResumeData(data);
              }}
              onError={(err: string) => {
                // show a small toast or console
                console.error('Resume parse error:', err);
              }}
            />
          </div>

          <CandidateProfileTabs
            candidateUuid={candidateUuid}
            isEditable={candidateUuid === 'me'}
            currentUser={currentUser}
            extractedResumeData={extractedResumeData}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {isLoggedIn && <CandidateHeader />}
      <div className={`max-w-4xl mx-auto space-y-6 ${isLoggedIn ? 'pt-8' : 'py-8'} px-4`}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Candidate Profile</h1>
            <p className="text-gray-600 mt-1">Manage your professional information and career details</p>
          </div>
          
          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={saving}
                  className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 disabled:opacity-50"
                >
                  <X className="w-4 h-4 mr-2 inline" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin mr-2 inline" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2 inline" />
                      Save Changes
                    </>
                  )}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                <Edit className="w-4 h-4 mr-2 inline" />
                Edit Profile
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-6 mb-6">
                <div className="relative">
                  <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center">
                    {profile?.basic?.avatar_url ? (
                      <img src={profile.basic.avatar_url} alt="Profile" className="w-20 h-20 rounded-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-gray-600" />
                    )}
                  </div>
                  {isEditing && (
                    <button className="absolute bottom-0 right-0 bg-blue-600 text-white rounded-full p-1 hover:bg-blue-700">
                      <Camera className="w-3 h-3" />
                    </button>
                  )}
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Professional Profile
                  </h3>
                  <p className="text-gray-600">{profile?.basic?.current_title || 'No title specified'}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <Building className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{profile?.basic?.current_company || 'No company specified'}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Title</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profile?.basic?.current_title || ''}
                      onChange={(e) => handleInputChange('current_title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="flex items-center">
                      <Briefcase className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900">{profile?.basic?.current_title || 'Not specified'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Company</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profile?.basic?.current_company || ''}
                      onChange={(e) => handleInputChange('current_company', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="flex items-center">
                      <Building className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900">{profile?.basic?.current_company || 'Not specified'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profile?.basic?.current_location || ''}
                      onChange={(e) => handleInputChange('current_location', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900">{profile?.basic?.current_location || 'Not specified'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                  {isEditing ? (
                    <input
                      type="url"
                      value={profile?.basic?.linkedin_url || ''}
                      onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://linkedin.com/in/your-profile"
                    />
                  ) : (
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      {profile?.basic?.linkedin_url ? (
                        <a href={profile.basic.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          LinkedIn Profile
                        </a>
                      ) : (
                        <span className="text-gray-900">Not provided</span>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GitHub</label>
                  {isEditing ? (
                    <input
                      type="url"
                      value={profile?.basic?.github_url || ''}
                      onChange={(e) => handleInputChange('github_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://github.com/your-username"
                    />
                  ) : (
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 text-gray-400 mr-2" />
                      {profile?.basic?.github_url ? (
                        <a href={profile.basic.github_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          GitHub Profile
                        </a>
                      ) : (
                        <span className="text-gray-900">Not provided</span>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Portfolio</label>
                  {isEditing ? (
                    <input
                      type="url"
                      value={profile?.basic?.portfolio_url || ''}
                      onChange={(e) => handleInputChange('portfolio_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://your-portfolio.com"
                    />
                  ) : (
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-gray-400 mr-2" />
                      {profile?.basic?.portfolio_url ? (
                        <a href={profile.basic.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          Portfolio Website
                        </a>
                      ) : (
                        <span className="text-gray-900">Not provided</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Professional Summary</label>
                {isEditing ? (
                  <textarea
                    value={profile?.basic?.summary || ''}
                    onChange={(e) => handleInputChange('summary', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Tell us about your professional background and expertise..."
                  />
                ) : (
                  <p className="text-gray-700 leading-relaxed">
                    {profile?.basic?.summary || 'No summary provided'}
                  </p>
                )}
              </div>
            </div>

            {/* Account Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">Profile Created:</span>
                  </div>
                  <span className="text-sm text-gray-900">
                    {profile?.basic?.created_at ? new Date(profile.basic.created_at).toLocaleDateString() : 'Unknown'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center">
                    <Settings className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">Last Updated:</span>
                  </div>
                  <span className="text-sm text-gray-900">
                    {profile?.basic?.updated_at ? new Date(profile.basic.updated_at).toLocaleDateString() : 'Never'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center">
                    <Shield className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">Profile Status:</span>
                  </div>
                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Statistics */}
          <div className="space-y-6">
            {/* Profile Completion */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Overview</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <GraduationCap className="w-5 h-5 text-blue-600" />
                    <div>
                      <div className="font-medium text-gray-900">Education</div>
                      <div className="text-sm text-gray-600">{profile?.education?.total_count || 0} entries</div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Briefcase className="w-5 h-5 text-green-600" />
                    <div>
                      <div className="font-medium text-gray-900">Work Experience</div>
                      <div className="text-sm text-gray-600">{profile?.work_experience?.total_count || 0} positions</div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Zap className="w-5 h-5 text-purple-600" />
                    <div>
                      <div className="font-medium text-gray-900">Skills</div>
                      <div className="text-sm text-gray-600">{profile?.skills?.total_count || 0} skills</div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Trophy className="w-5 h-5 text-orange-600" />
                    <div>
                      <div className="font-medium text-gray-900">Awards</div>
                      <div className="text-sm text-gray-600">{profile?.awards_achievements?.total_count || 0} awards</div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Award className="w-5 h-5 text-indigo-600" />
                    <div>
                      <div className="font-medium text-gray-900">Certifications</div>
                      <div className="text-sm text-gray-600">{profile?.certifications?.total_count || 0} certificates</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button 
                  onClick={() => handleQuickAction('education')}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  <GraduationCap className="w-4 h-4 mr-2 inline" />
                  Manage Education
                </button>
                <button 
                  onClick={() => handleQuickAction('experience')}
                  className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                >
                  <Briefcase className="w-4 h-4 mr-2 inline" />
                  Update Experience
                </button>
                <button 
                  onClick={() => handleQuickAction('skills')}
                  className="w-full bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors"
                >
                  <Zap className="w-4 h-4 mr-2 inline" />
                  Add Skills
                </button>
                <button 
                  onClick={() => handleQuickAction('awards')}
                  className="w-full bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors"
                >
                  <Trophy className="w-4 h-4 mr-2 inline" />
                  Add Awards
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
